import {
    type ChainAvailabilityItem,
    chainAvailabilityResponseSchema,
} from '@/models/appointment-chain-availability.ts';

import { InsuranceType } from '@/utils/constants.js';
import { JourneyType, processAPIWithHeadersForPractitioners } from '@/utils/helpers.js';

const CHAIN_AUTO_SUGGEST_ENDPOINT = 'public/users/availability/chain-availability/auto-suggest';

type InsuranceTypeValue = (typeof InsuranceType)[keyof typeof InsuranceType];

export type ChainAutoSuggestionRequestStep = {
    stepOrder: number;
};

export type ChainAutoSuggestionParams = {
    customerId: string;
    insuranceType: InsuranceTypeValue;
    isReturning: boolean;
    instanceId: string;
    chainId: string;
    startDate?: string; // YYYY-MM-DD; omitted on the first call
    steps: ChainAutoSuggestionRequestStep[];
    mainPractitionerId?: string; // present for a specific pick; omitted for no-preference
};

/**
 * Requests the next chain auto-suggestion for a customer/chain/insurance.
 *
 * `processAPIWithHeadersForPractitioners` returns `res.json()` even on 4xx/5xx, so a malformed or
 * error body fails the `safeParse` and surfaces as a thrown `Error` here.
 */
export const getChainAutoSuggestion = async (params: ChainAutoSuggestionParams): Promise<ChainAvailabilityItem[]> => {
    const { customerId, insuranceType, isReturning, instanceId, chainId, startDate, steps, mainPractitionerId } =
        params;

    if (!customerId) {
        throw new Error('chain-availability: missing customer_id');
    }

    const body: Record<string, unknown> = {
        insurance_type: insuranceType,
        is_returning: isReturning,
        instance_id: instanceId,
        chain_id: chainId,
        journey_type: JourneyType.SEARCH_BOOK,
        steps: steps.map(({ stepOrder }) => ({ step_order: stepOrder })),
    };

    if (startDate !== undefined) {
        body.start_date = startDate;
    }

    if (mainPractitionerId) {
        body.main_practitioner_id = mainPractitionerId;
    }

    const response = await processAPIWithHeadersForPractitioners(CHAIN_AUTO_SUGGEST_ENDPOINT, 'POST', {
        body: body,
        headers: { 'Content-Type': 'application/json', customer_id: customerId },
    });

    const parsed = chainAvailabilityResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('chain-availability: invalid auto-suggestion response:', parsed.error.issues);
        throw new Error('chain-availability: invalid auto-suggestion response');
    }

    return parsed.data.data;
};
